import { useMemo, useState } from 'react';

import { Gate } from '../../../global/contexts/auth/auth';
import { IconScale } from '../../../common/icons';
import { formatDateOnly } from '../../../common/utils/date';
import { fmtMoney } from '../../../common/utils/money';
import { usePregnanciesList } from '../contexts/pregnancies';
import { useValuations } from '../../animals/contexts/valuations';
import ValuationForm from '../../animals/forms/valuation-form';

import '../../../common/styles/catalog.css';
import './styles/donor-panel.css';

// Preñeces ACTIVAS (en curso) de una donadora: GET /admin/pregnancies?donor=<id>
// (admin-pregnancies.md; el `donor` matchea por lineage efectivo del embrión), filtrado
// a estados en curso (open/confirmed) — excluye lost/born. Read-only, permiso
// pregnancies:read. Cada preñez (cría FUTURA) se puede AVALUAR (0108, ruta espejo
// /admin/pregnancies/:id/valuations). Se monta en la ficha de la donadora (solo hembras).
const STATE = {
    open:      { label: 'En curso',   cls: 'cat-badge--1' },
    confirmed: { label: 'Confirmada', cls: 'cat-badge--2' },
};
const ACTIVE = new Set(['open', 'confirmed']);

const recipientLabel = (p) => (p.recipient
    ? `${p.recipient.number ?? '—'}${p.recipient.name ? ` · ${p.recipient.name}` : ''}`
    : '—');

// Valor actual (último avalúo status=2) de una preñez.
function PregValue({ pregnancyId }) {
    const { data } = useValuations('pregnancies', pregnancyId);
    const v = data?.latest?.estimated_value;
    return v != null ? <strong>{fmtMoney(v)}</strong> : <span className="cat-table__empty">—</span>;
}

export default function DonorPregnanciesPanel({ animalId }) {
    const { data, isLoading, error } = usePregnanciesList({ donor: animalId, limit: 200 });
    const [valuing, setValuing] = useState(null); // preñez a avaluar

    const items = useMemo(() => {
        const list = (data?.items || []).filter((p) => ACTIVE.has(p.state));
        // más próximas a parir primero (estimated asc); sin fecha estimada al final.
        return list.sort((a, b) => String(a.estimated || '9999').localeCompare(String(b.estimated || '9999')));
    }, [data]);

    return (
        <section className="cat-detail__card">
            <div className="dpreg__head">
                <h2 className="cat-detail__section-title">Preñeces activas</h2>
                {!isLoading && !error && <span className="dpreg__count">{items.length}</span>}
            </div>

            {error ? (
                <p className="dpreg__muted">{error.status === 403 ? 'Sin permiso para ver preñeces.' : (error.message || 'No se pudieron cargar las preñeces.')}</p>
            ) : isLoading ? (
                <p className="dpreg__muted">Cargando preñeces…</p>
            ) : !items.length ? (
                <p className="dpreg__muted">Sin preñeces activas.</p>
            ) : (
                <table className="cat-table cat-table--compact">
                    <thead>
                        <tr><th>Receptora</th><th>Finca</th><th>Toro</th><th>Parto est.</th><th>Estado</th><th>Valor</th><th className="cat-table__actions-col" aria-label="Acciones" /></tr>
                    </thead>
                    <tbody>
                        {items.map((p) => {
                            const st = STATE[p.state] || { label: p.state, cls: 'cat-badge--1' };
                            const parto = p.estimated || p.due;
                            return (
                                <tr key={p.id} className="cat-table__row">
                                    <td>{recipientLabel(p)}</td>
                                    <td>{p.recipient_farm?.name || <span className="cat-table__empty">—</span>}</td>
                                    <td>{p.sire?.name || <span className="cat-table__empty">—</span>}</td>
                                    <td>{parto ? formatDateOnly(parto) : <span className="cat-table__empty">—</span>}</td>
                                    <td>
                                        <span className={`cat-badge ${st.cls}`}>{st.label}</span>
                                        {p.pending && <span className="dpreg__flag" title="Palpación vencida">· dx vencido</span>}
                                    </td>
                                    <td><PregValue pregnancyId={p.id} /></td>
                                    <td className="cat-table__actions">
                                        <Gate domain="animals" action="write">
                                            <button type="button" className="cat-table__edit" onClick={() => setValuing(p)} aria-label="Avaluar preñez" title="Avaluar preñez"><IconScale /></button>
                                        </Gate>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}

            {valuing && (
                <ValuationForm
                    subject={{ kind: 'pregnancies', id: valuing.id, label: `Preñez · ${recipientLabel(valuing)}` }}
                    onClose={() => setValuing(null)}
                    onSaved={() => setValuing(null)}
                />
            )}
        </section>
    );
}
